import { useState, useEffect } from 'react'
import { detectCoordinateFormat, parseCoordinate } from '../../utils/geo/formats'
import { ddToMgrs, formatMgrs } from '../../utils/geo/mgrs'
import type { CoordinateFormat } from '../../types/geo'
import { useToast } from '../../hooks/useToast'

interface Props {
  value?: string
  onChange?: (value: string) => void
  onPickOnMap?: () => void
  placeholder?: string
}

const FORMAT_LABELS: Record<CoordinateFormat, string> = {
  dd: 'DD',
  dms: 'DMS',
  mgrs: 'MGRS',
  utm: 'UTM',
  geojson: 'GeoJSON',
}

export default function CoordinateInput({ value = '', onChange, onPickOnMap, placeholder }: Props) {
  const [raw, setRaw] = useState(value)
  const [detectedFmt, setDetectedFmt] = useState<CoordinateFormat | null>(null)
  const [isValid, setIsValid] = useState<boolean | null>(null)
  const { toast: _toast } = useToast()

  useEffect(() => {
    setRaw(value)
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setRaw(v)
    if (onChange) onChange(v)

    if (!v.trim()) {
      setDetectedFmt(null)
      setIsValid(null)
      return
    }

    const fmt = detectCoordinateFormat(v)
    setDetectedFmt(fmt)

    if (fmt) {
      const parsed = parseCoordinate(v)
      setIsValid(!!parsed)
    } else {
      setIsValid(false)
    }
  }

  const parsed = raw ? parseCoordinate(raw) : null
  const ddCoord = parsed?.format === 'dd' ? parsed : null
  const mgrsPreview = ddCoord ? ddToMgrs(ddCoord.lat, ddCoord.lon) : null

  return (
    <div className="space-y-2">
      <div className="relative flex items-center gap-2">
        <input
          type="text"
          value={raw}
          onChange={handleChange}
          placeholder={placeholder ?? '38.889, -77.035 or 18S UJ 23395 09512'}
          className={`flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm font-mono shadow-sm transition-colors
            placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring
            ${isValid === false ? 'border-destructive' : isValid === true ? 'border-green-500' : 'border-input'}`}
        />
        {detectedFmt && (
          <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium
            ${isValid ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
            {FORMAT_LABELS[detectedFmt]}
          </span>
        )}
      </div>

      {mgrsPreview && (
        <div className="text-xs font-mono text-muted-foreground">
          MGRS: {formatMgrs(mgrsPreview)}
        </div>
      )}

      {onPickOnMap && (
        <button
          type="button"
          onClick={onPickOnMap}
          className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
        >
          Pick on Map
        </button>
      )}
    </div>
  )
}
